import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Service, Category } from '../../types';
import { servicesApi } from '../../firebase/services';
import ImageUpload from './ImageUpload';
import TranslationTabs, { TranslationValues } from './TranslationTabs';

interface ServiceManagementProps {
  services: Service[];
  categories: Category[];
  onRefresh: () => void;
}

const ServiceManagement: React.FC<ServiceManagementProps> = ({
  services,
  categories,
  onRefresh
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [formData, setFormData] = useState<Partial<Service>>({
    name: '',
    description: '',
    duration: 60,
    price: { min: 0, max: 0 },
    category: 'Haircuts',
    imageUrl: '',
    popular: false,
    showPrice: true,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [translations, setTranslations] = useState<TranslationValues>({ fr: {}, es: {} });

  const handleOpenDialog = (service?: Service) => {
    if (service) {
      setSelectedService(service);
      setFormData({
        name: service.name,
        description: service.description,
        duration: service.duration,
        price: { ...service.price },
        category: service.category,
        imageUrl: service.imageUrl,
        popular: service.popular,
        showPrice: service.showPrice !== false,
      });
      setTranslations({
        fr: { name: service.translations?.fr?.name || '', description: service.translations?.fr?.description || '' },
        es: { name: service.translations?.es?.name || '', description: service.translations?.es?.description || '' },
      });
    } else {
      setSelectedService(null);
      setFormData({
        name: '',
        description: '',
        duration: 60,
        price: { min: 0, max: 0 },
        category: categories[0]?.name || '',
        imageUrl: '',
        popular: false,
        showPrice: true,
      });
      setTranslations({ fr: {}, es: {} });
    }
    setSelectedImage(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError(null);
  };

  const handleOpenDeleteDialog = (service: Service) => {
    setSelectedService(service);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (type: 'min' | 'max', value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      price: {
        ...(prev.price || { min: 0, max: 0 }),
        [type]: numValue
      }
    }));
  };

  const handlePopularChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, popular: e.target.checked }));
  };

  const handleSubmit = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Validate form data
      if (!formData.name || !formData.description || !formData.duration) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.price?.min === undefined || formData.price?.max === undefined) {
        throw new Error('Please enter valid price range');
      }

      if (formData.price.min > formData.price.max) {
        throw new Error('Minimum price cannot be greater than maximum price');
      }

      if (selectedService) {
        // Update existing service
        let imageUrl = formData.imageUrl;

        // Upload image if selected
        if (selectedImage) {
          setUploadingImage(true);
          imageUrl = await servicesApi.uploadImage(selectedImage, selectedService.id);
          setUploadingImage(false);
        }

        const serviceData = { ...formData, imageUrl, translations };
        await servicesApi.update(selectedService.id, serviceData);
      } else {
        // Create new service first to get the ID
        const serviceData = { ...formData, imageUrl: formData.imageUrl || '', translations };
        const newServiceId = await servicesApi.create(serviceData as Service);

        // Then upload image if selected
        if (selectedImage) {
          setUploadingImage(true);
          const imageUrl = await servicesApi.uploadImage(selectedImage, newServiceId);
          setUploadingImage(false);
          
          // Update service with image URL
          await servicesApi.update(newServiceId, { imageUrl });
        }
      }

      onRefresh();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving service:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
      setUploadingImage(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;

    setIsLoading(true);
    try {
      await servicesApi.delete(selectedService.id);
      onRefresh();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting service:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated services
  const paginatedServices = services.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Services
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Service
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Price Range</TableCell>
              <TableCell>Popular</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedServices.map((service) => (
              <TableRow key={service.id}>
                <TableCell>{service.name}</TableCell>
                <TableCell>{service.category}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                    {service.duration} min
                  </Box>
                </TableCell>
                <TableCell>${service.price.min} - ${service.price.max}</TableCell>
                <TableCell>
                  {service.popular ? (
                    <Chip label="Popular" color="primary" size="small" />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(service)}
                    title="Edit Service"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDeleteDialog(service)}
                    title="Delete Service"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {services.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No services found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={services.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Lignes par page :"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
      />

      {/* Service Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedService ? 'Edit Service' : 'Add New Service'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ width: '100%' }}>
              <TextField
                name="name"
                label="Service Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  name="category"
                  value={formData.category}
                  label="Category"
                  onChange={handleSelectChange}
                >
                  {categories.length === 0 && (
                    <MenuItem value="" disabled>
                      Aucune catégorie — créez-en une d'abord
                    </MenuItem>
                  )}
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: '100%' }}>
              <TextField
                name="description"
                label="Description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={3}
                required
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <TranslationTabs
                fields={[
                  { key: 'name', label: 'Nom du service' },
                  { key: 'description', label: 'Description', multiline: true, rows: 3 },
                ]}
                values={translations}
                onChange={setTranslations}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ width: { xs: '100%', sm: '33%' } }}>
                <TextField
                  name="duration"
                  label="Duration (minutes)"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputProps={{ inputProps: { min: 15, step: 5 } }}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '33%' } }}>
                <TextField
                  name="price.min"
                  label="Minimum Price ($)"
                  type="number"
                  value={formData.price?.min}
                  onChange={(e) => handlePriceChange('min', e.target.value)}
                  fullWidth
                  required
                  InputProps={{ inputProps: { min: 0, step: 5 } }}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '33%' } }}>
                <TextField
                  name="price.max"
                  label="Maximum Price ($)"
                  type="number"
                  value={formData.price?.max}
                  onChange={(e) => handlePriceChange('max', e.target.value)}
                  fullWidth
                  required
                  InputProps={{ inputProps: { min: 0, step: 5 } }}
                />
              </Box>
            </Stack>
            <Box sx={{ width: '100%' }}>
              <ImageUpload
                currentImageUrl={formData.imageUrl}
                onImageSelected={(file) => setSelectedImage(file)}
                onImageRemoved={() => {
                  setSelectedImage(null);
                  setFormData(prev => ({ ...prev, imageUrl: '' }));
                }}
                label="Image du service"
              />
              {uploadingImage && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Upload de l'image en cours...
                </Alert>
              )}
            </Box>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!formData.popular}
                    onChange={handlePopularChange}
                  />
                  Marquer comme service populaire (affiché sur la page d'accueil)
                </label>
              </FormControl>
              <FormControl>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.showPrice !== false}
                    onChange={(e) => setFormData(prev => ({ ...prev, showPrice: e.target.checked }))}
                  />
                  Afficher le prix sur le site public
                </label>
              </FormControl>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isLoading}>
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
            {isLoading && (
              <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-10px', marginLeft: '-10px' }} />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the service "{selectedService?.name}"? This action cannot be undone.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isLoading}>
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </Button>
            {isLoading && (
              <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-10px', marginLeft: '-10px' }} />
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceManagement;
